/**
 * Move notation.
 *
 * Neither previous build could express a game as text — moves existed only as pairs of
 * cell indices into a particular board ordering. That made games unshareable, logs
 * unreadable, and bug reports impossible to write down.
 *
 * Cells are named the way hexagonal chess names them (Glinski's convention): a letter
 * file taken from `q`, and a rank counted up each column from its bottom cell. The
 * centre of a radius-5 board is `f6`.
 *
 *   f6-g6    a step
 *   f6xg6    a capture
 *   f6xi3    a capture at the end of a glide — distance says it was a bow (§4)
 *   f6*g6    a spawn: the king on f6 places a pawn on g6
 *   pass
 *   resign
 *
 * with `+` appended for check and `#` for checkmate.
 *
 * Spawns name their king because two kings adjacent to the same empty cell generate two
 * distinct moves. They reach identical positions, but the move log records which king
 * acted, so the notation does too.
 */

import { type Board, getBoard, MAX_RADIUS } from './board.ts';
import type { Move, Variant } from './types.ts';
import {
  type FastPosition,
  isKing,
  makeMove,
  MOVE_PASS,
  MOVE_SPAWN,
  moveFrom,
  moveTo,
  moveType,
  unmakeMove,
} from './position.ts';
import { isCheckmate, isInCheck, legalMoves, packFromMove } from './movegen.ts';

const FILES = 'abcdefghijklm';

if (FILES.length < 2 * MAX_RADIUS + 1) {
  throw new Error('Not enough file letters for the largest supported board');
}

/** Highest `r` in column `q` is the bottom of that column; rank 1 sits there. */
const topOfColumn = (radius: number, q: number): number => Math.min(radius, radius - q);

/** The name of a cell, e.g. `f6`. */
export function cellName(board: Board, index: number): string {
  const { q, r } = board.hexAt(index);
  const file = FILES[q + board.radius];
  const rank = topOfColumn(board.radius, q) - r + 1;
  return `${file}${rank}`;
}

/** The cell a name refers to, or `undefined` if it is not on this board. */
export function cellIndex(board: Board, name: string): number | undefined {
  const match = /^([a-m])(\d{1,2})$/.exec(name.trim().toLowerCase());
  if (!match) return undefined;

  const q = FILES.indexOf(match[1]) - board.radius;
  const rank = Number(match[2]);
  const r = topOfColumn(board.radius, q) - rank + 1;

  const index = board.indexAt(q, r);
  return index < 0 ? undefined : index;
}

/** Every legal cell name on a board, in board order. Useful for tests and tooling. */
export const allCellNames = (variant: Variant): string[] => {
  const board = getBoard(variant.radius);
  return Array.from({ length: board.size }, (_, i) => cellName(board, i));
};

/**
 * Render a move. Needs the position it is played in, to tell a capture from a step and
 * to know whether it gives check.
 */
export function formatMove(pos: FastPosition, move: Move): string {
  const packed = packFromMove(move);
  const type = moveType(packed);

  if (type === MOVE_PASS) return 'pass';
  if (type === 3) return 'resign';

  const board = pos.board;
  const from = cellName(board, moveFrom(packed));
  const to = cellName(board, moveTo(packed));

  let text: string;
  if (type === MOVE_SPAWN) {
    text = `${from}*${to}`;
  } else {
    const captures = pos.cells[moveTo(packed)] !== 0;
    text = `${from}${captures ? 'x' : '-'}${to}`;
  }

  // Look ahead one ply for the check/checkmate suffix.
  const undo = makeMove(pos, packed);
  const suffix = isCheckmate(pos) ? '#' : isInCheck(pos) ? '+' : '';
  unmakeMove(pos, packed, undo);

  return text + suffix;
}

/**
 * Parse a move in the position it is played in, returning `undefined` if it is not a
 * legal move there. Check and checkmate suffixes are accepted and ignored — they are
 * derived, not part of the move's identity.
 */
export function parseMove(pos: FastPosition, text: string): Move | undefined {
  const cleaned = text.trim().toLowerCase().replace(/[+#]$/, '');

  if (cleaned === 'pass') return { type: 'pass' };
  if (cleaned === 'resign') return { type: 'resign' };

  const match = /^([a-m]\d{1,2})([-x*])([a-m]\d{1,2})$/.exec(cleaned);
  if (!match) return undefined;

  const fromIndex = cellIndex(pos.board, match[1]);
  const toIndex = cellIndex(pos.board, match[3]);
  if (fromIndex === undefined || toIndex === undefined) return undefined;

  const wanted: Move = match[2] === '*'
    ? { type: 'spawnPiece', fromIndex, toIndex }
    : { type: 'movePiece', fromIndex, toIndex };

  const target = packFromMove(wanted);
  return legalMoves(pos).some((m) => packFromMove(m) === target) ? wanted : undefined;
}

/**
 * Render a whole game as numbered turns, ready to paste into a bug report.
 *
 * Mutates and restores `pos`, which must be the position the moves start from.
 */
export function formatGame(pos: FastPosition, moves: readonly Move[]): string {
  const parts: string[] = [];
  const played: number[] = [];
  const undos: number[] = [];

  for (const move of moves) {
    if (pos.playerToMove === 0) parts.push(`${played.length / 2 + 1}.`);
    parts.push(formatMove(pos, move));

    const packed = packFromMove(move);
    undos.push(makeMove(pos, packed));
    played.push(packed);
  }

  for (let i = played.length - 1; i >= 0; i--) unmakeMove(pos, played[i], undos[i]);

  return parts.join(' ');
}

/** A short description of a piece, for interface copy. */
export const pieceName = (encoded: number): string =>
  encoded === 0 ? 'empty' : isKing(encoded) ? 'king' : 'pawn';
